import { Alert, Button, Modal } from 'antd'
import { CircularProgress, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
    ModalState,
    addGeneralAssembly,
    closeStartGeneralAssemblyActivityModal,
    updateStartGeneralAssemblyActivityModal,
} from '../../../../store/assembly-worksheets-slice'
import { toastFailure } from '../../../../util/toast'
import { useEffect, useState } from 'react'
import {
    getUserCurrentActiveAssemblyActivityRequest,
    startNewActiveAssemblyActivityRequest,
} from '../../../../api/assembly-activites'
import { AssemblyActivity, GeneralAssembly } from '../../../../models/models'
import { ActivityType } from '../../../../models/models'
import {
    GeneralAssemblyRelation,
    getGeneralAssemblyByIdRequest,
} from '../../../../api/general-assemblies'

export function GeneralAssemblyActivityStartModal() {
    const dispatch = useAppDispatch()
    const { generalAssemblyId, state } = useAppSelector(
        (state) => state.assemblyWorksheets.startGeneralAssemblyActivityModalState
    )
    const authenticatedUser = useAppSelector((state) => state.authentication.user)

    const [generalAssembly, setGeneralAssembly] = useState<GeneralAssembly>()
    const [currentActivity, setCurrentActivity] = useState<AssemblyActivity>()

    const initiateModal = async () => {
        if (state === ModalState.Closed) {
            return
        }
        if (!generalAssemblyId || !authenticatedUser) {
            toastFailure('Failed to retrieve ressources necessary for modal')
            dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Error }))
            return
        }

        const [currentAcivityResponse, generalAssemblyResponse] = await Promise.all([
            getUserCurrentActiveAssemblyActivityRequest(),
            getGeneralAssemblyByIdRequest(generalAssemblyId, {
                joins: [
                    GeneralAssemblyRelation.Bom,
                    GeneralAssemblyRelation.CreatedBy,
                    GeneralAssemblyRelation.Lines,
                    GeneralAssemblyRelation.LinesAssembledBy,
                    GeneralAssemblyRelation.Activities,
                    GeneralAssemblyRelation.ActivitiesUser,
                ],
            }),
        ])

        if (!currentAcivityResponse.successful || !generalAssemblyResponse.successful) {
            toastFailure('Failed to retrieve the users activity or the worksheet')
            dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Error }))
            return
        }

        dispatch(addGeneralAssembly(generalAssemblyResponse.data))

        const currentActivity = currentAcivityResponse.data
        const worksheet = generalAssemblyResponse.data

        // check if the worksheet has an activity where the user is the owner that has not ended
        const alreadyActive = worksheet.activities.some((activity) => {
            if (activity.userId === authenticatedUser.id && !activity.endedAt) {
                toastFailure('You already have an activity for this worksheet')
                dispatch(closeStartGeneralAssemblyActivityModal())
                return
            }
        })

        if (alreadyActive) {
            toastFailure('User already active on the worksheet')
            dispatch(closeStartGeneralAssemblyActivityModal())
            return
        }

        setCurrentActivity(currentActivity)
        setGeneralAssembly(worksheet)
        dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Ready }))
    }

    const startActivity = async () => {
        if (!generalAssemblyId) {
            toastFailure('Failed to start activity')
            dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Error }))
            return
        }
        dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Loading }))

        const response = await startNewActiveAssemblyActivityRequest({
            assemblyId: generalAssemblyId,
            type: ActivityType.GeneralAssembly,
        })

        if (!response.successful) {
            dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Error }))
            toastFailure(response.message)
            return
        }

        const generalAssemblyResponse = await getGeneralAssemblyByIdRequest(generalAssemblyId, {
            joins: [
                GeneralAssemblyRelation.Bom,
                GeneralAssemblyRelation.CreatedBy,
                GeneralAssemblyRelation.Lines,
                GeneralAssemblyRelation.LinesAssembledBy,
                GeneralAssemblyRelation.Activities,
                GeneralAssemblyRelation.ActivitiesUser,
            ],
        })

        if (!generalAssemblyResponse.successful) {
            toastFailure('Failed to retrieve the worksheet after updating the activity')
            dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Error }))
            return
        }

        dispatch(addGeneralAssembly(generalAssemblyResponse.data))
        dispatch(closeStartGeneralAssemblyActivityModal())
    }

    useEffect(() => {
        if (state === ModalState.Opened) {
            dispatch(updateStartGeneralAssemblyActivityModal({ state: ModalState.Loading }))
            initiateModal()
        }
    }, [state])

    const footer = (
        <div>
            <Button
                disabled={false}
                onClick={() => dispatch(closeStartGeneralAssemblyActivityModal())}
            >
                Inspect
            </Button>
            <Button type="primary" disabled={state !== ModalState.Ready} onClick={startActivity}>
                Claim Worksheet
            </Button>
        </div>
    )

    const getModalText = () => {
        if (currentActivity) {
            const activityTypeText =
                currentActivity.type === ActivityType.GeneralAssembly ? (
                    <Typography>
                        You already have an active activity on another general assembly <br />
                        ID: {currentActivity.generalAssemblyId} <br />
                        Item:
                        {currentActivity.generalAssembly.builtItem.bomCode}
                    </Typography>
                ) : (
                    <Typography>
                        You already have an active activity on another assembly <br />
                        ID: {currentActivity.generalAssemblyId} <br />
                        Item:{' '}
                        {
                            currentActivity.assembly.builtItemCallOff.sopBuiltItemOrderline
                                .builtItem.bomCode
                        }
                    </Typography>
                )
            return (
                <>
                    <Alert
                        message={
                            <Typography>
                                {activityTypeText} <br />
                                By claiming this worksheet, you will leave the one you are currently
                                active on
                            </Typography>
                        }
                        type="warning"
                    />
                    <br />
                    <Typography>
                        You can either inspect the activity or claim the worksheet to start a new
                        activity.
                    </Typography>
                </>
            )
        }

        return (
            <div>
                <Typography>
                    By claiming this worksheet, you will be able to start completing units
                </Typography>
            </div>
        )
    }

    return (
        <Modal
            title={<Typography sx={{ fontWeight: 'bold' }}>{'Start General Assembly'}</Typography>}
            open={state !== ModalState.Closed}
            width={700}
            onCancel={() => dispatch(closeStartGeneralAssemblyActivityModal())}
            footer={footer}
        >
            <div>
                {state === ModalState.Error && <div>Failed to load the modal</div>}
                {state === ModalState.Loading && (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={75} style={{ color: 'rgb(53, 0, 160)' }} />
                    </div>
                )}
                {state === ModalState.Ready && <div>{getModalText()}</div>}
            </div>
        </Modal>
    )
}
